import { useEffect, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { navigate } from '../../app/hashRoute';
import { apiClient } from '../../core/api';
import type { ApiModel } from '../../core/api';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import type { ComponentMetaInput } from '../../ui';
import { contentStyle, marketplaceCardMeta, marketplaceMeta } from './componentMeta';
import { formatPrice } from '../../core/formatPrice';
import { isOfficialImageModel } from '../chat/officialModelCatalog';
import styles from '../account/commerce.module.css';

function Registered({ meta, children, className, surface, flow = false }: { meta: ComponentMetaInput; children?: ReactNode; className?: string; surface?: 'page' | 'panel' | 'control' | 'overlay'; flow?: boolean }): ReactElement {
  const complete = buildComponentMeta(meta, meta.role ?? 'container');
  const ref = useComponentMeta<HTMLDivElement>(complete);
  return <div ref={ref} className={className} data-ui-id={complete.id} data-ui-role={complete.role} data-skin-surface={surface} style={flow ? undefined : contentStyle(meta.layout)}>{children}</div>;
}

function ModelCard({ model, index }: { model: ApiModel; index: number }): ReactElement {
  const imageModel = isOfficialImageModel(model);
  return <Registered meta={marketplaceCardMeta(index)} className={styles.model} flow>
    <h2 className={styles.modelName}>{model.displayName}</h2>
    <p className={styles.muted}>{model.provider} · {model.slug} · {model.protocol}</p>
    {imageModel ? <dl className={styles.imagePrices}>
      {(['1k', '2k', '4k'] as const).map((size) => {
        const price = model.price?.[`image${size}`];
        return <div key={size}><dt>{size}</dt><dd>{price == null ? '价格暂不可用' : `${formatPrice(price)} 栀籽 / 张`}</dd></div>;
      })}
    </dl> : <>
      {model.description ? <p className={styles.modelDescription}>{model.description}</p> : null}
      <div className={styles.tags}>{model.capabilities.map((tag) => <span className={styles.tag} key={tag}>{tag}</span>)}</div>
      <p className={styles.price}>{model.price ? `输入 ${formatPrice(model.price.inputPerMillion)} / 输出 ${formatPrice(model.price.outputPerMillion)} / 缓存输入 ${formatPrice(model.price.cachedInputPerMillion)} 栀籽 / 百万 Token` : '价格暂不可用'}</p>
      <p className={styles.muted}>上下文 {model.contextTokens.toLocaleString()} · 最大输出 {model.maxOutputTokens.toLocaleString()}</p>
    </>}
  </Registered>;
}

export function MarketplacePage(): ReactElement {
  const [models, setModels] = useState<ApiModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void apiClient.listModels().then((items) => { if (active) setModels(items.filter((item) => item.isPublished)); }).catch((requestError: unknown) => { if (active) setError(requestError instanceof Error ? requestError.message : '模型列表加载失败'); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  return <Registered meta={marketplaceMeta('marketplace.root')} className={styles.page} surface="page">
    <Registered meta={marketplaceMeta('marketplace.header')} className={styles.header} />
    <Registered meta={marketplaceMeta('marketplace.title')} className={styles.title}>栀歆模型广场</Registered>
    <div className={styles.scroll}>
      {loading ? <p className={styles.notice}>正在加载栀歆模型…</p> : error ? <div className={styles.notice} role="alert"><p>{error}</p></div> : models.length === 0 ? <div className={styles.notice}><p>暂时没有已上架模型。</p><button className={styles.secondary} type="button" onClick={() => navigate('/account')}>前往账户</button></div> : models.map((model, index) => <ModelCard key={model.id} model={model} index={index} />)}
    </div>
  </Registered>;
}
